import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2DataGrid,
  V2EmptyState,
  V2Kpi,
  type V2BadgeTone,
  type V2GridColumn,
  type V2GridTone,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { downloadCsv, type CsvColumn } from '../../lib/tableExport.js';
import type { StockBalanceRow, StockBalancesResponse } from '../../lib/types/inventory.js';
import { quantityToNumber } from '../../lib/types/inventory.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import {
  STOCK_BALANCES_LIMIT,
  formatQuantity,
  isStockBalancesCapped,
  quantitySign,
  stockStatusOf,
  type StockStatus,
} from './inventoryDisplay.js';
import './inventory.css';

const STATUS_TONE: Record<StockStatus, V2BadgeTone> = {
  کافی: 'success',
  کم‌موجود: 'warn',
  ناموجود: 'danger',
};

/** Row dot in the grid's sticky code column — only the two states that need attention. */
const STATUS_DOT: Record<StockStatus, V2GridTone | undefined> = {
  کافی: undefined,
  کم‌موجود: 'warn',
  ناموجود: 'danger',
};

const stockStatus = stockStatusOf;

const fa = (n: number) => n.toLocaleString('fa-IR');
const STOCK_TONE_CLASS: Record<StockStatus, string> = {
  کافی: '',
  کم‌موجود: 'inv-tone-warn',
  ناموجود: 'inv-tone-down',
};

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Cloud ERP v2.dc.html §/t/kimia/inventory — stock table
 *   ("موجودی کالا — همهٔ انبارها"), the product owner's pattern for every
 *   list table (V2DataGrid). Detail panel: Sakhtar Inventory.dc.html §01.
 * archetype: ENTITY_LIST (V2DataGrid) + inline detail
 * status: KIT_CONFORMANT — real omissions (no backing field in the API):
 *   - "گروه", "واحد": `baseUomId`/category are camelCase product fields the
 *     master-data field-visibility policy can never grant (its DB check
 *     constraint only accepts lowercase names) — same root cause documented
 *     in ERP-223-design-v2.md for partyId/productId/warehouseId.
 *   - "نقطهٔ سفارش", "پوشش": no reorder-point field exists on a product, so
 *     no coverage meter and no reorder-point-driven status; status is the
 *     real 3-state from onHand vs. available.
 *   - "ارزش (ریال)": no per-balance valuation in the stock-balances response.
 *   - "آخرین حرکت": no endpoint returns movements filtered by product.
 *   - Totals row: row count only. Rows mix products with different (and
 *     unexposed) units, so a summed quantity would add kilograms to pieces —
 *     not shown rather than misleading.
 *   - "ستون‌ها" is V2DataGrid's column chooser (remembered per browser
 *     under `inventory-stock`); "خروجی" exports the visible rows as CSV.
 * state reference: ready · empty · permission_denied · network_error
 */
export function StockPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const [query, setQuery] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const result = useApiGet<StockBalancesResponse>(
    slug ? `${base}/api/v1/inventory/stock-balances?limit=${STOCK_BALANCES_LIMIT}` : null,
    [slug],
  );
  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?limit=200` : null,
    [slug],
  );

  const productNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    if (products.status === 'ready') {
      for (const p of products.data.items) {
        if (typeof p.code === 'string' && typeof p.name === 'string') map.set(p.code, p.name);
      }
    }
    return map;
  }, [products]);

  const warehouseOptions = useMemo(() => {
    if (result.status !== 'ready') return [];
    return [
      ...new Set(result.data.balances.map((b) => b.warehouseCode).filter((c): c is string => !!c)),
    ];
  }, [result]);

  const rows = useMemo(() => {
    if (result.status !== 'ready') return [];
    const q = query.trim().toLowerCase();
    return result.data.balances.filter((row) => {
      if (warehouseFilter && row.warehouseCode !== warehouseFilter) return false;
      if (statusFilter && stockStatus(row) !== statusFilter) return false;
      if (q) {
        const name = row.productCode ? (productNameByCode.get(row.productCode) ?? '') : '';
        if (!name.toLowerCase().includes(q) && !(row.productCode ?? '').toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [result, query, warehouseFilter, statusFilter, productNameByCode]);

  const rowKey = (row: StockBalanceRow) =>
    `${row.productId}:${row.warehouseId}:${row.warehouseLocationId}:${row.lotId ?? ''}:${row.serialId ?? ''}`;
  const selected = rows.find((r) => rowKey(r) === selectedKey) ?? null;
  const productName = (row: StockBalanceRow) =>
    row.productCode ? (productNameByCode.get(row.productCode) ?? row.productCode) : '—';

  if (!slug) return null;

  const columns: V2GridColumn<StockBalanceRow>[] = [
    {
      key: 'code',
      header: 'کد کالا',
      width: 104,
      render: (r) => <span className="ltr sk-num inv-code">{r.productCode ?? '—'}</span>,
    },
    {
      key: 'name',
      header: 'کالا',
      width: 170,
      render: (r) => <span className="inv-cell__main">{productName(r)}</span>,
      footer: <span className="inv-cell__sub">{fa(rows.length)} ردیف</span>,
    },
    { key: 'warehouse', header: 'انبار', width: 64, render: (r) => r.warehouseCode ?? '—' },
    {
      key: 'onHand',
      header: 'موجودی واقعی',
      align: 'end',
      emphasis: 'group',
      width: 84,
      render: (r) => <span className="sk-num">{formatQuantity(r.onHand)}</span>,
    },
    {
      key: 'reserved',
      header: 'رزرو',
      align: 'end',
      emphasis: 'group',
      width: 84,
      render: (r) => <span className="sk-num">{formatQuantity(r.reserved)}</span>,
    },
    {
      key: 'blocked',
      header: 'مسدود',
      align: 'end',
      emphasis: 'group',
      width: 84,
      render: (r) => (
        <span className={`sk-num ${quantitySign(r.blocked) > 0 ? 'inv-tone-warn' : ''}`}>
          {formatQuantity(r.blocked)}
        </span>
      ),
    },
    {
      key: 'available',
      header: 'قابل استفاده',
      align: 'end',
      emphasis: 'key',
      width: 84,
      render: (r) => (
        <span className={`sk-num ${STOCK_TONE_CLASS[stockStatus(r)]}`}>
          {formatQuantity(r.available)}
        </span>
      ),
    },
  ];

  const csvColumns: CsvColumn<StockBalanceRow>[] = [
    { header: 'کد کالا', value: (r) => r.productCode },
    { header: 'کالا', value: productName },
    { header: 'انبار', value: (r) => r.warehouseCode },
    { header: 'موجودی واقعی', value: (r) => quantityToNumber(r.onHand) },
    { header: 'رزرو', value: (r) => quantityToNumber(r.reserved) },
    { header: 'مسدود', value: (r) => quantityToNumber(r.blocked) },
    { header: 'قابل استفاده', value: (r) => quantityToNumber(r.available) },
    { header: 'وضعیت', value: stockStatus },
  ];

  return (
    <div className="inv-page">
      {result.status === 'loading' ? <LoadingState /> : null}
      {result.status === 'error' ? (
        <StatePanel
          variant={result.error.status === 403 ? 'permission_denied' : 'network_error'}
          title={result.error.status === 403 ? 'دسترسی ندارید' : 'موجودی قابل نمایش نیست'}
        />
      ) : null}

      {result.status === 'ready' && isStockBalancesCapped(result.data.balances.length) ? (
        <V2Alert tone="warn">
          فقط {fa(STOCK_BALANCES_LIMIT)} ردیفی که اخیراً تغییر کرده‌اند نمایش داده می‌شود؛ برای
          بقیهٔ اقلام از جست‌وجوی کاردکس کالا استفاده کنید.
        </V2Alert>
      ) : null}

      {result.status === 'ready' ? (
        <div className="inv-split">
          <div className="inv-split__main">
            <V2DataGrid
              icon="box"
              storageKey="inventory-stock"
              title={
                warehouseFilter
                  ? `موجودی کالا — انبار ${warehouseFilter}`
                  : 'موجودی کالا — همهٔ انبارها'
              }
              subtitle={`${fa(rows.length)} ردیف در انبارهای در دسترس شما`}
              actions={
                <V2Button
                  variant="secondary"
                  size="sm"
                  icon="upload"
                  disabled={rows.length === 0}
                  onClick={() => downloadCsv('stock-balances', csvColumns, rows)}
                >
                  خروجی
                </V2Button>
              }
              search={{ value: query, onChange: setQuery, placeholder: 'کد یا نام کالا' }}
              filters={[
                ...(warehouseOptions.length > 1
                  ? [
                      {
                        id: 'warehouse',
                        label: 'انبار',
                        value: warehouseFilter,
                        onChange: setWarehouseFilter,
                        options: [
                          { value: '', label: 'همه' },
                          ...warehouseOptions.map((w) => ({ value: w, label: w })),
                        ],
                      },
                    ]
                  : []),
                {
                  id: 'status',
                  label: 'وضعیت',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'همه' },
                    { value: 'کافی', label: 'کافی' },
                    { value: 'کم‌موجود', label: 'کم‌موجود' },
                    { value: 'ناموجود', label: 'ناموجود' },
                  ],
                },
              ]}
              columns={columns}
              rows={rows}
              rowKey={rowKey}
              rowTone={(r) => STATUS_DOT[stockStatus(r)]}
              onRowClick={(r) => setSelectedKey(rowKey(r))}
              selectedKey={selectedKey}
              footerLabel="جمع"
              legend={[
                { tone: 'danger', label: 'قابل استفاده صفر' },
                { tone: 'warn', label: 'بخشی از موجودی رزرو یا مسدود است' },
              ]}
              emptyState={
                <V2EmptyState
                  icon="inventory"
                  title="هنوز موجودی‌ای برای نمایش نیست"
                  description="پس از ثبت اولین رسید یا حواله، موجودی کالاها اینجا نمایش داده می‌شود."
                />
              }
            />
          </div>

          {selected ? (
            <div className="inv-split__side">
              <V2Card>
                <V2CardHeader
                  icon="box"
                  title={productName(selected)}
                  subtitle={
                    <span className="ltr sk-num">
                      {selected.productCode ?? '—'} · {selected.warehouseCode ?? '—'}
                    </span>
                  }
                  actions={
                    <V2Badge tone={STATUS_TONE[stockStatus(selected)]}>
                      {stockStatus(selected)}
                    </V2Badge>
                  }
                />
                <div className="inv-kpi-row">
                  <V2Kpi label="موجودی کل" value={formatQuantity(selected.onHand)} />
                  <V2Kpi label="رزروشده" value={formatQuantity(selected.reserved)} />
                  <V2Kpi label="قابل استفاده" value={formatQuantity(selected.available)} />
                </div>
                <div className="inv-form--card">
                  {/* No product-filter query param exists on ReservationsPage
                      yet, so this lands on the unfiltered list — still
                      strictly better than no link at all. */}
                  <Link
                    to={`${base}/inventory/reservations`}
                    className="sk-btn sk-btn--secondary sk-btn--sm inv-btn-link inv-btn-link--block"
                  >
                    مشاهدهٔ رزروها
                  </Link>
                </div>
              </V2Card>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
